import React from 'react'
import { PhoneIncoming, PhoneOutgoing, PhoneOff, Clock } from 'lucide-react'
import { CallDirection } from '@super/shared-types'
import { formatMessageTime } from '@/utils/date-formatters'
import { TimeDisplay } from '@/components/common/TimeDisplay'
import { MessageActionsMenu } from '../MessageActionsMenu'

interface CallMessageProps {
  isCallStart: boolean
  duration?: number
  createdAt: Date
  threadId?: string
  messageId?: string
  companyId?: string
  direction?: CallDirection
  originatingCaller?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
}

export const CallMessage: React.FC<CallMessageProps> = ({
  isCallStart,
  duration,
  createdAt,
  threadId,
  messageId,
  companyId,
  direction,
  originatingCaller,
  sessionId,
  onSessionDetailsClick,
}) => {
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getCallStartDisplay = () => {
    if (direction === CallDirection.INBOUND) {
      return { Icon: PhoneIncoming, text: 'Incoming call started' }
    }
    if (direction === CallDirection.OUTBOUND) {
      return { Icon: PhoneOutgoing, text: 'Outgoing call started' }
    }
    return { Icon: PhoneIncoming, text: 'Call started' }
  }

  const getCallEndDisplay = () => {
    if (direction === CallDirection.INBOUND) {
      return { Icon: PhoneOff, text: 'Incoming call ended' }
    }
    if (direction === CallDirection.OUTBOUND) {
      return { Icon: PhoneOff, text: 'Outgoing call ended' }
    }
    return { Icon: PhoneOff, text: 'Call ended' }
  }

  const { Icon: StartIcon, text: startText } = getCallStartDisplay()
  const { Icon: EndIcon, text: endText } = getCallEndDisplay()

  return (
    <div className="mb-4 flex justify-center group relative">
      <div className="max-w-[80%] flex flex-col items-center gap-1 relative group/card">
        {threadId && messageId && (
          <div className="absolute -right-14 z-30">
            <MessageActionsMenu
              threadId={threadId}
              messageId={messageId}
              companyId={companyId}
              sessionId={sessionId}
              onSessionDetailsClick={onSessionDetailsClick}
              layout="row"
              tooltipSide="top"
            />
          </div>
        )}
        <div className="flex flex-col items-center">
          <div className="bg-gray-800 text-xs text-gray-100 border border-gray-900 px-2 py-1.5 flex items-center gap-2 font-xs rounded-lg">
            {isCallStart ? (
              <>
                <StartIcon className="w-4 h-4" />
                <span>{startText}</span>
              </>
            ) : (
              <>
                <EndIcon className="w-4 h-4" />
                <span>{endText}</span>
                {duration && (
                  <>
                    <Clock className="w-4 h-4 ml-2" />
                    <span>{formatDuration(duration)}</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <TimeDisplay
          date={createdAt}
          formatter={date => {
            const timeStr = formatMessageTime(date)
            return originatingCaller
              ? `${timeStr} by ${originatingCaller}`
              : timeStr
          }}
          className="text-xs text-gray-500 dark:text-muted-foreground text-center block"
        />
      </div>
    </div>
  )
}
